// src/pages/Welcome.jsx
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Plug, Sparkles, PenLine } from 'lucide-react';
import { useGuide } from '../contexts/GuideContext';
import { services } from '../services/productBrain';
import { prefersReducedMotion } from '../components/guide/motion';
import './Welcome.css';

const CHIPS = ['Grow self-serve activation', 'Reduce churn', 'Ship the platform migration', 'Reduce employee task time', 'Improve service quality'];
const quarter = d => `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`;

export default function Welcome() {
  const guide = useGuide();
  const navigate = useNavigate();
  const reduced = prefersReducedMotion();
  const [stage, setStage] = useState('film');
  const [filmFailed, setFilmFailed] = useState(false);
  const [goal, setGoal] = useState('');
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const typing = useRef(null);
  const startedAt = guide?.activation?.started_at;
  useEffect(() => { if (guide && !startedAt) guide.update({ started_at: new Date().toISOString() }); }, [guide, startedAt]);
  useEffect(() => () => clearInterval(typing.current), []);
  if (!guide) return null;

  const typeIn = text => {
    clearInterval(typing.current);
    if (reduced) { setGoal(text); return; }
    let i = 0;
    typing.current = setInterval(() => { i += 1; setGoal(text.slice(0, i)); if (i >= text.length) clearInterval(typing.current); }, 28);
  };
  const saveGoal = async e => {
    e.preventDefault(); setBusy('goal'); setError('');
    try {
      if (!goal.trim()) throw new Error('Name a goal, or choose “Not now”.');
      const objective = await services.objectives.create({ title: goal.trim(), description: 'Set during onboarding.', quarter: quarter(new Date()), status: 'active', key_results: [] });
      await guide.update({ goal_objective_id: objective.id });
      setStage('doors');
    } catch (err) { setError(err.message); } finally { setBusy(''); }
  };
  const door = async kind => {
    setBusy(kind); setError('');
    try {
      if (kind === 'jira') { await guide.update({ path: 'jira' }); navigate('/onboarding'); }
      if (kind === 'sample') { await guide.seed(); navigate('/'); }
      if (kind === 'scratch') { await guide.update({ path: 'scratch' }); guide.openComposer(); navigate('/'); }
    } catch (err) { setError(err.message || 'Something went wrong. Try again.'); } finally { setBusy(''); }
  };
  const skip = async () => { await guide.update(a => ({ path: a.path || 'scratch' })); navigate('/'); };

  return <div className="welcome">
    <button type="button" className="welcome-skip v2-text-action" onClick={skip}>Skip for now</button>
    {stage === 'film' && <section className="welcome-film" aria-label="Introduction">
      {reduced || filmFailed
        ? <img src="/media/spcc-intro-poster.jpg" alt="SPCC connects signals into one product loop: better decisions, less noise."/>
        : <video src="/media/spcc-intro.mp4" poster="/media/spcc-intro-poster.jpg" muted autoPlay playsInline onEnded={() => setStage('goal')} onError={() => setFilmFailed(true)} aria-label="SPCC introduction film"/>}
      <button type="button" className="v2-button" onClick={() => setStage('goal')}>{reduced || filmFailed ? 'Continue' : 'Skip intro'}<ArrowRight size={16}/></button>
    </section>}
    {stage === 'goal' && <form className="welcome-goal" onSubmit={saveGoal}>
      <span className="v2-eyebrow">STEP 1 OF 2 · YOUR ANCHOR</span>
      <h1>What’s your #1 goal this quarter?</h1>
      <p>Give your decisions an outcome to work toward. You can adapt SPCC to your product in Setup & integrations.</p>
      <label className="v2-field"><span>Your #1 goal this quarter</span><input value={goal} onChange={e => { clearInterval(typing.current); setGoal(e.target.value); }} placeholder="e.g. Grow self-serve activation"/></label>
      <div className="welcome-chips">{CHIPS.map(chip => <button type="button" key={chip} className="welcome-chip" onClick={() => typeIn(chip)}>{chip}</button>)}</div>
      {error && <p className="v2-error" role="alert">{error}</p>}
      <div className="v2-actions"><button type="submit" className="v2-button" disabled={busy === 'goal'}>{busy === 'goal' ? 'Saving…' : 'Save goal'}</button><button type="button" className="v2-button secondary" onClick={() => setStage('doors')}>Not now</button></div>
    </form>}
    {stage === 'doors' && <section className="welcome-doors" aria-label="How do you want to start?">
      <span className="v2-eyebrow">STEP 2 OF 2 · PICK A DOOR</span>
      <h1>How do you want to start?</h1>
      {error && <p className="v2-error" role="alert">{error}</p>}
      <div className="welcome-door-grid">
        <button type="button" className="welcome-door" disabled={!!busy} onClick={() => door('jira')}><Plug/><strong>Set up your product</strong><span>Choose your context and connect the sources you already use.</span></button>
        <button type="button" className="welcome-door featured" disabled={!!busy} onClick={() => door('sample')}><Sparkles/><strong>{busy === 'sample' ? 'Building Pathwise…' : 'Explore a sample product'}</strong><span>See a full Morning Brief in seconds. Remove it anytime.</span></button>
        <button type="button" className="welcome-door" disabled={!!busy} onClick={() => door('scratch')}><PenLine/><strong>Start from scratch</strong><span>Capture one observation, request, or customer quote.</span></button>
      </div>
    </section>}
  </div>;
}
